import { centerOfMass } from '@turf/turf';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  cn,
  Divider,
  RiArrowRightUpLine,
  RiCompass4Line,
  Stack,
} from 'component-library';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { UnitEnum } from '@/api/rest/resources/types/units';
import { TooltipContentWidget, TooltipTriggerWidget, TooltipWidget } from '@/components/widgets/m/Tooltip/Tooltip';
import { NO_DATA_PLACEHOLDER } from '@/config/constants';
import {
  InteractiveMapCellProperties,
  InteractiveMapFormFields,
  useInteractiveMapControlsContext,
} from '@/pages/shared/plots/pages/hooks/useControlsForm';
import {
  FactCapsule,
  FactCapsuleTrend,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { Area, Dimension } from '@/utils/area';
import { isNullish } from '@/utils/booleans';

const UNIT_MAP: Partial<{
  [key in UnitEnum]: Dimension;
}> = {
  [UnitEnum.ha]: 'hectares',
  [UnitEnum['km^2']]: 'squareKilometers',
  [UnitEnum['m^2']]: 'squareMeters',
} as const;

export const InfoPanel = () => {
  const { t } = useTranslation();

  const controlsForm = useInteractiveMapControlsContext();
  const cell = controlsForm.watch(InteractiveMapFormFields.Cell);

  const areaUnits = useCellFact('area_units');
  let dimension: Dimension = 'squareKilometers';
  if (!isNullish(areaUnits.value)) {
    dimension = UNIT_MAP[areaUnits.value as keyof typeof UNIT_MAP] ?? dimension;
  }

  const areaDisplay = cell?.properties?.area_cut
    ? new Area({
        value: cell?.properties.area_cut,
        dimension,
      }).printDisplay()
    : NO_DATA_PLACEHOLDER;

  if (!cell) {
    return null;
  }

  const cellCoordinates = centerOfMass(cell).geometry.coordinates.map((c) => c.toFixed(6));
  const cellCoordinatesDisplay = `${cellCoordinates[0]} S, ${cellCoordinates[1]} E`;

  const closePanel = () => {
    controlsForm.setValue(InteractiveMapFormFields.Cell, undefined);
  };

  return (
    <Stack className='h-full w-[413px] bg-white-100' data-testid='interactive-map-info-panel'>
      <Stack spacing={2} className='flex-1 overflow-auto pb-8'>
        <Stack spacing={4} className='mx-6 mt-8 rounded-xl bg-neutral-black-4 p-4'>
          <Stack direction='row' centerMain spacing={8}>
            <InfoStack
              label={t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.cellId')}
              content={cell?.properties?.cell_id ?? NO_DATA_PLACEHOLDER}
            />
            <InfoStack
              label={t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.unitSize')}
              content={areaDisplay}
              tooltip={t('shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.area.description')}
            />
          </Stack>
          <InfoStack
            label={t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.cellCoordinates')}
            content={cellCoordinatesDisplay}
          />
        </Stack>

        <Accordion className='group/accordion flex flex-col px-4' type='single' collapsible>
          <AccordionItem value='species'>
            <AccordionTrigger>
              {/* eslint-disable-next-line sonarjs/no-duplicate-string */}
              {t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.habitatIntactness')}
            </AccordionTrigger>
            <AccordionContent>
              <HabitatIntactness />
            </AccordionContent>
            <AccordionDivider />
          </AccordionItem>
        </Accordion>
      </Stack>
      <Stack
        direction='row'
        className={cn('justify-end justify-self-end px-6 py-4', '[box-shadow:_0px_-10px_10px_0px_rgba(0,0,0,0.05)]')}
      >
        <Button variant='outline' onClick={closePanel}>
          {t('global.ui.buttons.close')}
        </Button>
      </Stack>
    </Stack>
  );
};

const InfoStack = ({
  label,
  content,
  tooltip,
}: {
  label: React.ReactNode;
  content: React.ReactNode;
  tooltip?: string;
}) => {
  return (
    <Stack spacing={2} className='min-w-0 flex-1'>
      <span
        className='typography-overline truncate text-text-secondary'
        title={typeof label === 'string' ? label : undefined}
      >
        {label}
        {tooltip && (
          <TooltipWidget>
            <TooltipTriggerWidget>
              <i className='ri-information-line ml-2 text-text-secondary' />
            </TooltipTriggerWidget>
            <TooltipContentWidget>
              <Stack spacing={1}>
                <span className='typography-body2'>{tooltip}</span>
              </Stack>
            </TooltipContentWidget>
          </TooltipWidget>
        )}
      </span>
      <span className='typography-h4 truncate' title={typeof content === 'string' ? content : undefined}>
        {content}
      </span>
    </Stack>
  );
};

const AccordionDivider = () => <Divider className='mx-4' />;

const LAST_AVERAGE_VALUE_LABEL =
  'shared.projects.project.conservation.interactiveMap.infoPanel.labels.latestAverageValue';
const AVERAGE_VALUE_TITLE_LABEL =
  'shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.averageValue.title';
const AVERAGE_VALUE_DESCRIPTION_LABEL =
  'shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.averageValue.description';

const HabitatIntactness = () => {
  const { t } = useTranslation();

  const hiLatestYear = useCellFact('target_year')?.value;
  const hiBaselineYear = useCellFact('base_year')?.value;

  const yearCount =
    !isNullish(hiLatestYear) && !isNullish(hiBaselineYear)
      ? parseInt(hiLatestYear, 10) - parseInt(hiBaselineYear, 10)
      : '';

  const hiLatestValue = useCellFact('habitat_intactness_latest');

  const hiBaselineValue = useCellFact('habitat_intactness_baseline');

  const hiTrendValue = useCellFact('habitat_intactness_trend');
  return (
    <Stack spacing={7}>
      <FactCapsule
        label={t(LAST_AVERAGE_VALUE_LABEL, {
          year: hiLatestYear,
        })}
        fact={hiLatestValue}
        capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
        infoPopoverProps={{
          title: t(AVERAGE_VALUE_TITLE_LABEL, {
            year: hiLatestYear,
          }),
          body: t(AVERAGE_VALUE_DESCRIPTION_LABEL, {
            year: hiLatestYear,
            indicator: t(
              'shared.projects.project.conservation.interactiveMap.infoPanel.labels.habitatIntactness',
            ).toLowerCase(),
          }),
        }}
      />
      <FactCapsule
        label={t('shared.projects.project.conservation.interactiveMap.infoPanel.labels.countYearChange', {
          yearCount,
        })}
        trend={<FactCapsuleTrend trend={hiTrendValue} />}
        fact={hiBaselineValue}
        capsuleIconProps={{ icon: <RiArrowRightUpLine />, variant: 'default' }}
        infoPopoverProps={{
          title: t('shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.countYearChange.title', {
            yearCount,
          }),
          body: hiLatestYear
            ? t('shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.countYearChange.description', {
                yearCount: hiLatestYear,
                indicator: t(
                  'shared.projects.project.conservation.interactiveMap.infoPanel.labels.habitatIntactness',
                ).toLowerCase(),
              })
            : t(
                'shared.projects.project.conservation.interactiveMap.infoPanel.tooltip.countYearChange.descriptionNoYears',
                {
                  year: hiLatestYear,
                  indicator: t(
                    'shared.projects.project.conservation.interactiveMap.infoPanel.labels.habitatIntactness',
                  ).toLowerCase(),
                },
              ),
        }}
      />
    </Stack>
  );
};

const useCellFact = <T extends keyof InteractiveMapCellProperties>(property: T) => {
  const controlsForm = useInteractiveMapControlsContext();

  const cellProperties = controlsForm.watch(InteractiveMapFormFields.Cell)?.properties;

  // eslint-disable-next-line security/detect-object-injection
  const value = cellProperties?.[property] as InteractiveMapCellProperties[T] | undefined;

  return { name: property, value, unit: UnitEnum.none };
};
